import { errorInvalidPositiveIntegerField } from './builderFieldMessages'

/** BFP-1156 — which answer option collects free text on a Multiple Choice / Checkbox question. */
export type ChoiceTextResponseTarget = 'option' | 'other' | 'none'

/** Builder state for Add Text (min/max kept as raw input strings like other numeric fields). */
export interface ChoiceTextResponseConfig {
  target: ChoiceTextResponseTarget
  /** Set when `target` is `option`. */
  optionId?: string
  minCharacters: string
  maxCharacters: string
}

/** Wire shape on the instrument draft question (`null` = Add Text off). */
export interface ChoiceTextResponseDraft {
  target: ChoiceTextResponseTarget
  optionId?: string
  minCharacters?: number
  maxCharacters?: number
}

/** Matches the campaign service ceiling for Add Text character limits. */
export const CHOICE_TEXT_RESPONSE_MAX_CHARACTERS = 2000

export function isTextResponseOn(
  tr: ChoiceTextResponseConfig | null | undefined,
  target: ChoiceTextResponseTarget,
  optionId?: string,
): boolean {
  if (tr?.target !== target) return false
  return target !== 'option' || tr.optionId === optionId
}

/** Toggle Add Text for one option; turning it on replaces any other target (one per question). */
export function toggleTextResponse(
  current: ChoiceTextResponseConfig | null | undefined,
  target: ChoiceTextResponseTarget,
  on: boolean,
  optionId?: string,
): ChoiceTextResponseConfig | null {
  if (!on) return isTextResponseOn(current, target, optionId) ? null : (current ?? null)
  return {
    target,
    ...(target === 'option' ? { optionId } : {}),
    minCharacters: '',
    maxCharacters: '',
  }
}

/** Drop the Add Text target when its option or special option no longer exists. */
export function pruneTextResponse(
  tr: ChoiceTextResponseConfig | null | undefined,
  ctx: { optionIds: readonly string[]; other: boolean; noneOfAbove: boolean },
): ChoiceTextResponseConfig | null {
  if (!tr) return null
  if (tr.target === 'other') return ctx.other ? tr : null
  if (tr.target === 'none') return ctx.noneOfAbove ? tr : null
  return tr.optionId && ctx.optionIds.includes(tr.optionId) ? tr : null
}

function parseLimit(raw: string): number | undefined {
  const t = raw.trim()
  if (!/^[1-9]\d*$/.test(t)) return undefined
  return Number(t)
}

export function textResponseToDraft(
  tr: ChoiceTextResponseConfig | null | undefined,
): ChoiceTextResponseDraft | null {
  if (!tr) return null
  const min = parseLimit(tr.minCharacters)
  const max = parseLimit(tr.maxCharacters)
  return {
    target: tr.target,
    ...(tr.target === 'option' && tr.optionId ? { optionId: tr.optionId } : {}),
    ...(min == null ? {} : { minCharacters: min }),
    ...(max == null ? {} : { maxCharacters: max }),
  }
}

/**
 * Draft → builder. Questions saved before BFP-1156 omit `textResponse`; when they had Other on,
 * keep the always-on Other specify box by mapping it to an `other` target.
 */
export function textResponseFromDraft(
  raw: ChoiceTextResponseDraft | null | undefined,
  hasOtherOption: boolean | undefined,
): ChoiceTextResponseConfig | null {
  if (raw === undefined) {
    return hasOtherOption ? { target: 'other', minCharacters: '', maxCharacters: '' } : null
  }
  if (raw === null) return null
  return {
    target: raw.target,
    ...(raw.target === 'option' && raw.optionId ? { optionId: raw.optionId } : {}),
    minCharacters: raw.minCharacters == null ? '' : String(raw.minCharacters),
    maxCharacters: raw.maxCharacters == null ? '' : String(raw.maxCharacters),
  }
}

/** Builder save / inline validation for the Text Input Settings panel. */
export function errorTextResponseLimits(tr: ChoiceTextResponseConfig | null | undefined): string | null {
  if (!tr) return null
  const minFmt = errorInvalidPositiveIntegerField(tr.minCharacters)
  if (minFmt) return `Min characters: ${minFmt}`
  const maxFmt = errorInvalidPositiveIntegerField(tr.maxCharacters)
  if (maxFmt) return `Max characters: ${maxFmt}`
  const min = parseLimit(tr.minCharacters)
  const max = parseLimit(tr.maxCharacters)
  if (min != null && min > CHOICE_TEXT_RESPONSE_MAX_CHARACTERS) {
    return `Minimum characters cannot exceed ${CHOICE_TEXT_RESPONSE_MAX_CHARACTERS}`
  }
  if (max != null && max > CHOICE_TEXT_RESPONSE_MAX_CHARACTERS) {
    return `Maximum characters cannot exceed ${CHOICE_TEXT_RESPONSE_MAX_CHARACTERS}`
  }
  if (min != null && max != null && min > max) {
    return 'Minimum characters cannot exceed maximum characters'
  }
  return null
}

/** Respondent-facing label for the option that collects text (builder panel caption). */
export function textResponseOptionLabel(
  tr: ChoiceTextResponseConfig,
  options: readonly string[],
  optionIds: readonly string[],
): string {
  if (tr.target === 'other') return 'Other'
  if (tr.target === 'none') return 'None of the above'
  const i = optionIds.indexOf(tr.optionId ?? '')
  return (i >= 0 ? options[i] : '')?.trim() || 'this option'
}
